import { clsx } from 'clsx';

import {
  mapButtonStyles,
  mapBackgroundStyles,
  mapProgressStyles,
} from './overrideMappers';
import {
  useWhiteLabelTheme,
  WhiteLabelTheme,
} from '../../store/WhiteLabelProvider';

type ComponentType = 'button' | 'progress' | 'background';

type WhiteLabelOverrideProps =
  | {
      children: React.ReactNode;
      componentType: 'button';
      disabled: boolean; // required when type = button
      variant?: 'primary' | 'secondary';
    }
  | {
      children: React.ReactNode;
      componentType: Exclude<ComponentType, 'button'>;
    };

export type ThemeVars = Record<`--${string}`, string>;
export interface ThemeCss {
  vars: ThemeVars;
  overrides: string;
}

export const WhiteLabelOverride = (props: WhiteLabelOverrideProps) => {
  const { children, componentType } = props;
  const { theme, isTheme } = useWhiteLabelTheme();

  if (!isTheme) {
    return <>{children}</>;
  }

  const { vars: whiteLabelledStyles, overrides } = mapThemeToCss(theme, props);

  const variant =
    componentType === 'button' ? (props.variant ?? 'primary') : undefined;

  const isDisabled = componentType === 'button' && props.disabled;

  return (
    <div
      className={clsx(
        'white-label-scope',
        componentType,
        componentType === 'button' && variant,
        isDisabled && 'disabled',
      )}
      style={whiteLabelledStyles as React.CSSProperties}
    >
      {overrides && <style>{overrides}</style>}
      {children}
    </div>
  );
};

type ButtonMapper = (theme: WhiteLabelTheme, disabled: boolean) => ThemeCss;
type DefaultMapper = (theme: WhiteLabelTheme) => ThemeCss;

const themeMappers: {
  button: ButtonMapper;
  background: DefaultMapper;
  progress: DefaultMapper;
} = {
  button: mapButtonStyles,
  background: mapBackgroundStyles,
  progress: mapProgressStyles,
};

const mapThemeToCss = (
  theme: WhiteLabelTheme,
  props: WhiteLabelOverrideProps,
): ThemeCss => {
  switch (props.componentType) {
    case 'button':
      return themeMappers.button(theme, props.disabled);
    default:
      return themeMappers[props.componentType](theme);
  }
};
